import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkExistingAccount,
  createVirtualAccount,
} from "../redux/slices/virtualAccountSlice";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const BankTransfer = () => {
  const dispatch = useDispatch();
  const {
    data: virtualAccount,
    loading,
    error,
    hasChecked,
  } = useSelector((state) => state.virtualAccount);

  useEffect(() => {
    dispatch(checkExistingAccount());
  }, [dispatch]);

  const handleGenerateAccount = () => {
    dispatch(createVirtualAccount());
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Add this function to show loading state with message
  const renderLoadingState = (message) => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={3}
      gap={2}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  // Update the loading check
  if (loading && !hasChecked) {
    return renderLoadingState("Checking for existing account...");
  }

  return (
    <Card sx={{ maxWidth: 600, mx: "auto", my: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Bank Transfer
        </Typography>

        {hasChecked && !virtualAccount ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body1" gutterBottom>
              Generate a virtual account number to make deposits via bank
              transfer
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateAccount}
              disabled={loading}
              sx={{ position: "relative" }}
            >
              {loading ? (
                <>
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      left: "50%",
                      marginLeft: "-12px",
                    }}
                  />
                  Generating...
                </>
              ) : (
                "Generate Account Number"
              )}
            </Button>
            {loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Please wait while we generate your virtual account...
              </Typography>
            )}
          </Box>
        ) : virtualAccount ? (
          <>
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                Bank Name:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>{virtualAccount.bankName}</Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(virtualAccount.bankName)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                Account Number:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>{virtualAccount.accountNumber}</Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(virtualAccount.accountNumber)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                Account Name:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>{virtualAccount.accountName}</Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(virtualAccount.accountName)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                1. Copy the account details above
                <br />
                2. Make a transfer from your bank app or internet banking
                <br />
                3. Your account will be credited automatically
                <br />
                4. The minimum transfer amount is ₦100
              </Typography>
            </Alert>
          </>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BankTransfer;
